import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { type FC, type ReactNode, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	getValueFormSchema,
	type ValueFormModel,
} from "web_myplann/Portfolio/Forms/valueFormSchema";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";

const PageResources = Resources.PortfolioDetail.Property.ValueDrawer;

type Props = {
	open: boolean;
	onClose: () => void;
	title: string;
	valueLabel: string;
	currencySign: string;
	isPending: boolean;
	onSave: (value: number, onSaved: () => void) => void;
	defaultValue?: number | null;
	subtitle?: string;
	closeButton?: boolean;
	beforeInput?: ReactNode;
	afterInput?: ReactNode;
};

export const ValueDrawer: FC<Props> = ({
	open,
	onClose,
	title,
	valueLabel,
	currencySign,
	isPending,
	onSave,
	defaultValue,
	subtitle,
	closeButton,
	beforeInput,
	afterInput,
}) => {
	const { palette, spacingTokens } = useTheme();
	const { t } = useResource();

	const schema = useMemo(() => getValueFormSchema(t), [t]);

	const { control, handleSubmit, reset } = useForm<ValueFormModel>({
		resolver: zodResolver(schema),
		defaultValues: { value: defaultValue ?? undefined } as ValueFormModel,
	});

	useEffect(() => {
		if (open) {
			reset({ value: defaultValue ?? undefined } as ValueFormModel);
		}
	}, [open, defaultValue, reset]);

	const onSubmit = handleSubmit((formValues) => {
		onSave(formValues.value, () => {
			toast.success(t(PageResources.savedToast));
			onClose();
		});
	});

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={title}
			closeButton={closeButton}
		>
			<Stack component="form" onSubmit={onSubmit} pb={spacingTokens.lg}>
				{subtitle && (
					<Typography
						variant="caption"
						display="block"
						color={palette.grey[500]}
						px={spacingTokens.lg}
						pb={spacingTokens.md}
					>
						{subtitle}
					</Typography>
				)}
				{beforeInput}
				<FlatRowFormInput
					control={control}
					name="value"
					type="number"
					label={valueLabel}
					placeholder={t(PageResources.placeholder)}
					suffix={currencySign}
					max={ValidationLimits.Amount.Max}
					isLast={!afterInput}
				/>
				{afterInput}
				<Stack px={spacingTokens.lg} pt={spacingTokens.lg}>
					<Button type="submit" variant="primary" loading={isPending}>
						{t(PageResources.confirm)}
					</Button>
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
